/**
 * parseChatError — normalise every failure shape into a [ChatError](../../types/errors.ts).
 *
 * One parser per ENTRY POINT, sharing the same field readers.
 */

import { asRecord, asString } from 'src/content/ChatV2/lib/guards';
import {
  AUTH_FAILURE_STATUS,
  CHAT_ERROR_ORIGIN,
  type ChatError,
  STOPPED_STATUS_CODE
} from 'src/content/ChatV2/types/errors';
import { ErrorCodes } from 'src/utils/errorMappings';

type Bag = Record<string, unknown>;

function asCode(value: unknown): string | undefined {
  if (typeof value === 'string' && value !== '') {
    return value.toUpperCase();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

function readCode(bag: Bag | undefined): string | undefined {
  if (!bag) {
    return undefined;
  }
  return (
    asCode(bag.code) ??
    asCode(bag.status_code) ??
    asCode(bag.error_code) ??
    asCode(bag.status) ??
    asCode(asRecord(bag.error)?.code)
  );
}

function readDetails(bag: Bag | undefined): string | undefined {
  if (!bag) {
    return undefined;
  }
  return (
    asString(bag.description) ??
    asString(bag.status_details) ??
    asString(bag.detail) ??
    asString(bag.message) ??
    asString(asRecord(bag.error)?.message)
  );
}

/**
 * `io.doclm.run.error`'s `data` (or the whole envelope when `data` is absent).
 * Unknown/empty payload → the general AI error.
 */
export function parseRunError(data: unknown): ChatError {
  const bag = asRecord(data);
  return {
    code: readCode(bag) ?? ErrorCodes.GENERAL_AI_ERROR,
    origin: CHAT_ERROR_ORIGIN.RUN,
    details: readDetails(bag)
  };
}

/**
 * The chat backend's `error` frame (not doclm's `io.doclm.run.error`): the turn
 * failed on the way to the client, e.g. the response could not be delivered over
 * the socket. Fields sit on the envelope: `{event_type:'error', chat_id, rid, code,
 * description}`. Unknown/empty payload → the general AI error.
 */
export function parseTransportError(event: unknown): ChatError {
  const bag = asRecord(event);
  return {
    code: readCode(bag) ?? ErrorCodes.GENERAL_AI_ERROR,
    origin: CHAT_ERROR_ORIGIN.TRANSPORT,
    details: readDetails(bag)
  };
}

/**
 * A rejected `chat-msg` POST.
 */
export function parseHttpError(error: unknown): ChatError {
  const bag = asRecord(error);
  const body = asRecord(bag?.data);
  return {
    code: readCode(body) ?? readCode(bag) ?? ErrorCodes.GENERAL_AI_ERROR,
    origin: CHAT_ERROR_ORIGIN.HTTP,
    details: readDetails(body) ?? readDetails(bag)
  };
}

const AUTH_STATUSES: ReadonlySet<string> = new Set<string>([
  ...Object.values(AUTH_FAILURE_STATUS),
  '401'
]);

export function isAuthError(error: unknown): boolean {
  const bag = asRecord(error);
  if (!bag) {
    return false;
  }
  const candidates = [
    bag.status,
    bag.originalStatus,
    asRecord(bag.data)?.status
  ];
  return candidates.some((value) => {
    const code = asCode(value);
    return code !== undefined && AUTH_STATUSES.has(code);
  });
}

const CHAT_NOT_FOUND_STATUSES: ReadonlySet<string> = new Set<string>([
  ErrorCodes.CHAT_NOT_FOUND_ERROR,
  ErrorCodes.RESOURCE_NOT_FOUND_ERROR,
  ErrorCodes.NOT_FOUND,
  ErrorCodes.INVALID_ITEM,
  '404'
]);

/**
 * Did loading a chat fail because the chat does not exist (deleted, or a bad id)?
 * Reads the same status fields as `isAuthError`.
 */
export function isChatNotFoundError(error: unknown): boolean {
  const bag = asRecord(error);
  if (!bag) {
    return false;
  }
  const candidates = [
    bag.status,
    bag.originalStatus,
    asRecord(bag.data)?.status
  ];
  return candidates.some((value) => {
    const code = asCode(value);
    return code !== undefined && CHAT_NOT_FOUND_STATUSES.has(code);
  });
}

/**
 * Did the user stop this turn?
 */
export function isStoppedRow(row: Bag | undefined): boolean {
  return asCode(row?.status_code) === STOPPED_STATUS_CODE;
}

export function parseRowError(
  row: Bag | undefined,
  origin: ChatError['origin'] = CHAT_ERROR_ORIGIN.PERSISTED
): ChatError | undefined {
  if (!row) {
    return undefined;
  }
  const code = asCode(row.status_code);
  if (code === STOPPED_STATUS_CODE) {
    return undefined;
  }
  const failed =
    row.has_error === true || code !== undefined || row.status === 'error';
  if (!failed) {
    return undefined;
  }
  return {
    code: code ?? ErrorCodes.GENERAL_AI_ERROR,
    origin,
    details: asString(row.status_details) ?? asString(row.description)
  };
}

/**
 * The stream went silent — no frame for `STREAM_IDLE_TIMEOUT_MS` while the FSM was
 * still connecting/streaming (see `StreamRunner`'s watchdog).
 */
export function streamTimeoutError(): ChatError {
  return {
    code: ErrorCodes.GENERAL_AI_ERROR,
    origin: CHAT_ERROR_ORIGIN.TRANSPORT
  };
}
